import { useState, useEffect, useRef, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { DebugProvider, useDebug } from "./contexts/DebugContext";

// Pages
import Landing from "./pages/Landing";
import RoleSelection from "./pages/RoleSelection";
import StudentLogin from "./pages/StudentLogin";
import StudentDashboard from "./pages/StudentDashboard";
import ParentDashboard from "./pages/ParentDashboard";
import TeacherDashboard from "./pages/TeacherDashboard";
import SubmissionDetail from "./pages/SubmissionDetail";
import TestDetail from "./pages/TestDetail";
import AdminDashboard from "./pages/AdminDashboard";
import MathDrill from "./pages/MathDrill";
import DrillConfig from "./pages/DrillConfig";
import DrillStats from "./pages/DrillStats";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios
axios.defaults.withCredentials = true;

// WCAG: Skip Link Component
function SkipLink() {
  return (
    <a 
      href="#main-content" 
      className="skip-link"
      tabIndex={0}
    >
      Skip to main content
    </a>
  );
}

// WCAG: Screen Reader Announcement for loading states
function LoadingAnnouncement({ loading, message = "Loading content" }) {
  return (
    <div 
      role="status" 
      aria-live="polite" 
      aria-busy={loading}
      className="sr-only"
    >
      {loading ? message : "Content loaded"}
    </div>
  );
}

// Auth Context - shared state across all components
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    console.log("checkAuth: Starting auth check...");
    try {
      const response = await axios.get(`${API}/auth/me`);
      console.log("checkAuth: Got user:", response.data);
      setUser(response.data);
    } catch (error) {
      console.log("checkAuth: No session found");
      setUser(null);
    } finally {
      console.log("checkAuth: Setting loading to false");
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    sessionStorage.clear();
  };

  const updateUser = (newUser) => {
    console.log("updateUser: Setting user to:", newUser);
    setUser(newUser);
    setLoading(false);
  };

  const value = { user, loading, checkAuth, logout, updateUser };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Auth Callback Component
function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { updateUser } = useAuth();

  const { addLog } = useDebug();
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Check both hash and search for session_id
      const hash = window.location.hash;
      const search = window.location.search;
      console.log("AuthCallback: Processing auth...", { hash, search });
      
      let sessionId = null;
      
      // Try hash first
      const hashMatch = hash.match(/session_id=([^&]+)/);
      if (hashMatch) {
        sessionId = hashMatch[1];
      } else {
        // Try search query params
        const params = new URLSearchParams(search);
        sessionId = params.get("session_id");
      }
      
      if (sessionId) {
        console.log("AuthCallback: Found session_id, exchanging...");
        addLog('auth', 'Found session_id in URL, attempting exchange', { sessionId: sessionId.substring(0, 5) + '...' });
        
        try {
          const response = await axios.get(`${API}/auth/session`, {
            headers: { "X-Session-ID": sessionId }
          });
          
          console.log("AuthCallback: Session exchange successful:", response.data);
          addLog('auth', 'Session exchange successful', { role: response.data.role, userId: response.data.user_id });
          
          // Clear URL (hash and search)
          window.history.replaceState(null, "", window.location.pathname);
          
          // Update user state
          updateUser(response.data);
          
          // Navigate based on role
          if (!response.data.role) {
            console.log("AuthCallback: No role, redirecting to role selection");
            navigate("/select-role", { replace: true });
          } else {
            console.log("AuthCallback: Has role, redirecting to dashboard");
            navigate("/dashboard", { replace: true });
          }
        } catch (error) {
          console.error("AuthCallback: Auth error:", error);
          console.error("AuthCallback: Error details:", error.response?.data);
          addLog('error', 'Auth exchange failed', { 
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
          });
          toast.error("Authentication failed. Please try again.");
          navigate("/", { replace: true });
        }
      } else {
        console.log("AuthCallback: No session_id found in URL");
        addLog('auth', 'No session_id found in URL hash or search params');
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [navigate, updateUser]);

  return (
    <div className="min-h-screen flex items-center justify-center graph-paper-bg" role="main">
      <div className="text-center">
        <LoadingAnnouncement loading={true} message="Signing you in, please wait" />
        <div 
          className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"
          role="progressbar"
          aria-label="Signing in"
        ></div>
        <p className="text-gray-700">Signing you in...</p>
      </div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children, requiredRole }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      console.log("ProtectedRoute: No user, redirecting to home");
      navigate("/", { replace: true });
    } else if (!loading && user && !user.role && location.pathname !== "/select-role") {
      console.log("ProtectedRoute: No role, redirecting to role selection");
      navigate("/select-role", { replace: true });
    } else if (!loading && user && requiredRole && user.role !== requiredRole) {
      console.log("ProtectedRoute: Wrong role, redirecting to dashboard");
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate, location.pathname, requiredRole]);

  if (loading) {
    console.log("ProtectedRoute: Still loading...");
    return (
      <div className="min-h-screen flex items-center justify-center graph-paper-bg" role="main">
        <LoadingAnnouncement loading={true} message="Loading your dashboard" />
        <div 
          className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"
          role="progressbar"
          aria-label="Loading dashboard"
        ></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return children;
}

// Dashboard Router
function DashboardRouter() {
  const { user, logout, updateUser } = useAuth();
  
  if (!user?.role) {
    return <Navigate to="/select-role" replace />;
  }

  // Handler for parent to login as their student
  const handleLoginAsStudent = (studentData) => {
    updateUser(studentData);
  };

  switch (user.role) {
    case "student":
      return <StudentDashboard user={user} logout={logout} />;
    case "parent":
      return <ParentDashboard user={user} logout={logout} onLoginAsStudent={handleLoginAsStudent} />;
    case "teacher":
      return <TeacherDashboard user={user} logout={logout} />;
    default:
      return <Navigate to="/select-role" replace />;
  }
}

// App Router
function AppRouter() {
  const { user, loading, checkAuth, updateUser } = useAuth();
  const location = useLocation();

  // Check both location.hash and window.location.hash for OAuth callback
  const hashValue = location.hash || window.location.hash;
  const searchValue = location.search || window.location.search;
  const hasSessionId = hashValue?.includes('session_id=') || searchValue?.includes('session_id=');

  useEffect(() => {
    // Don't check auth if we're processing a session_id
    if (!hasSessionId) {
      console.log("AppRouter: Checking auth on mount");
      checkAuth();
    } else {
      console.log("AppRouter: Skipping auth check, has session_id in hash");
    }
  }, []);

  // Check URL fragment for session_id during render
  if (hasSessionId) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={
        user ? <Navigate to="/dashboard" replace /> : <Landing />
      } />
      
      {/* COPPA: Student login with access code (no OAuth for kids) */}
      <Route path="/student-login" element={
        user ? <Navigate to="/dashboard" replace /> : <StudentLogin onLoginSuccess={updateUser} />
      } />
      
      <Route path="/select-role" element={
        <ProtectedRoute>
          <RoleSelection />
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardRouter />
        </ProtectedRoute>
      } />
      
      <Route path="/submission/:id" element={
        <ProtectedRoute>
          <SubmissionDetail />
        </ProtectedRoute>
      } />
      
      <Route path="/test/:testId" element={
        <ProtectedRoute>
          <TestDetail />
        </ProtectedRoute>
      } />
      
      {/* Math Drill Routes */}
      <Route path="/drills" element={
        <ProtectedRoute>
          <DrillConfig />
        </ProtectedRoute>
      } />
      
      <Route path="/drills/:drillId" element={
        <ProtectedRoute>
          <MathDrill />
        </ProtectedRoute>
      } />
      
      <Route path="/drills/:drillId/stats" element={
        <ProtectedRoute>
          <DrillStats />
        </ProtectedRoute>
      } />
      
      {/* Admin Dashboard - protected, checks admin status on component mount */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminDashboardWrapper />
        </ProtectedRoute>
      } />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Admin Dashboard Wrapper - passes user and logout to AdminDashboard
function AdminDashboardWrapper() {
  const { user, logout } = useAuth();
  return <AdminDashboard user={user} logout={logout} />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <DebugProvider>
          <AuthProvider>
            <SkipLink />
            <AppRouter />
          </AuthProvider>
        </DebugProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}

export default App;
